import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  amendAdvance,
  cancelApprovedAdvance,
  getAdvanceDisbursementLedger,
  postponeAdvanceDisbursement,
  returnAdvanceDisbursement,
  returnAdvanceToHr,
} from "../../api/advances";
import { mapAdvanceRow } from "../../utils/dataMappers";
import { canHrApproveRequests } from "../../utils/hrStaffHelpers";
import {
  addDaysToDateKey,
  canAmendApprovedAdvance,
  canCancelApprovedAdvance,
  canPostponeAdvanceDisbursement,
  canReturnAdvanceToHr,
  canRouteAdvanceDisbursement,
  isAdvanceDisbursementPostponed,
  localDateKey,
} from "../../utils/advanceDisbursement";
import AdvancePostponeModal from "./AdvancePostponeModal";
import { exportExcel } from "../reports/reportExport";
import "./AdvanceDisbursementRouting.css";

function roundInstallment(total, count) {
  const value = Number(total) / Number(count);
  if (Number.isNaN(value)) return 0;
  return Math.round(value * 100) / 100;
}

function formatAmount(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `${value.toLocaleString("ar-EG")} ج.م`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("ar-EG");
}

function advanceTypeLabel(row) {
  return Number(row.installments) > 1 ? "سلفة مقسطة" : "سلفة عادية";
}

function personLabel(person) {
  if (!person?.fullName) return "—";
  return `${person.fullName}${person.jobTitle ? ` (${person.jobTitle})` : ""}`;
}

function assigneeLabel(row) {
  if (row.disbursementAssignee?.fullName) {
    return personLabel(row.disbursementAssignee);
  }
  if (row.disbursementAssigneeLabel) {
    return row.disbursementAssigneeLabel;
  }
  if (
    row.disbursementTargetType === "CASHIER" &&
    row.disbursementStatus === "ASSIGNED"
  ) {
    const branch = row.branchName || "الفرع";
    return `كاشيرية فرع ${branch}`;
  }
  return "—";
}

function statusMeta(row) {
  const status = row?.disbursementStatus;
  if (isAdvanceDisbursementPostponed(row)) {
    return { label: "مؤجلة الصرف", className: "is-pending" };
  }
  switch (status) {
    case "DISBURSED":
      return { label: "تم الصرف", className: "is-done" };
    case "ASSIGNED":
      return { label: "بانتظار تأكيد الاستلام", className: "is-assigned" };
    case "PENDING_ASSIGNMENT":
      return { label: "بانتظار توجيه الصرف", className: "is-pending" };
    case "RETURNED_TO_HR":
      return { label: "مُعادة للموارد البشرية", className: "is-pending" };
    default:
      return { label: status || "—", className: "" };
  }
}

const NO_BRANCH = "__no_branch__";
const NO_DEPARTMENT = "__no_department__";
const ALL_MONTHS = "all";

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "ar"),
  );
}

function rowBranchKey(row) {
  return row.branchName || NO_BRANCH;
}

function rowDepartmentKey(row) {
  return row.departmentName || NO_DEPARTMENT;
}

/** Day of actual cash-out when known; otherwise approval day. */
function rowDayAnchor(row) {
  return (
    row.disbursementConfirmedAt || row.approvedAt || row.submittedAt || null
  );
}

function toMonthKey(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

function toDayKey(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month) return monthKey;
  return new Date(year, month - 1, 1).toLocaleDateString("ar-EG", {
    month: "long",
    year: "numeric",
  });
}

function formatDayLabel(dayKey) {
  if (!dayKey) return "";
  const date = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dayKey;
  return date.toLocaleDateString("ar-EG", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function AdvanceDisbursementLedger() {
  const { user } = useAuth();
  const canAmend = canHrApproveRequests(user?.role);
  const canRoute = canRouteAdvanceDisbursement(user);
  const showActions = canAmend || canRoute;
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [monthFilter, setMonthFilter] = useState(null);
  const [dayFilter, setDayFilter] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [editModal, setEditModal] = useState({
    open: false,
    row: null,
    amount: "",
    installments: "",
    installmentAmount: "",
    notes: "",
  });
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const [rowBusyId, setRowBusyId] = useState("");
  const [rowBusyAction, setRowBusyAction] = useState("");
  const [postponeModal, setPostponeModal] = useState({
    open: false,
    row: null,
    date: "",
    reason: "",
    error: "",
  });

  const loadAdvances = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await getAdvanceDisbursementLedger();
      const list = (Array.isArray(rows) ? rows : rows?.items || []).map(
        mapAdvanceRow,
      );
      setAdvances(list);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل سجل صرف السلف",
      );
      setAdvances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  const monthTabs = useMemo(() => {
    const counts = new Map();
    for (const row of advances) {
      const key = toMonthKey(rowDayAnchor(row));
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, count]) => ({
        id: key,
        label: formatMonthLabel(key),
        count,
      }));
  }, [advances]);

  useEffect(() => {
    if (!monthTabs.length) {
      setMonthFilter(ALL_MONTHS);
      return;
    }
    setMonthFilter((prev) => {
      if (prev === ALL_MONTHS) return ALL_MONTHS;
      if (prev && monthTabs.some((tab) => tab.id === prev)) return prev;
      const current = currentMonthKey();
      if (monthTabs.some((tab) => tab.id === current)) return current;
      return monthTabs[0].id;
    });
  }, [monthTabs]);

  const monthScopedAdvances = useMemo(() => {
    if (dayFilter) {
      return advances.filter(
        (row) => toDayKey(rowDayAnchor(row)) === dayFilter,
      );
    }
    if (!monthFilter || monthFilter === ALL_MONTHS) return advances;
    return advances.filter(
      (row) => toMonthKey(rowDayAnchor(row)) === monthFilter,
    );
  }, [advances, monthFilter, dayFilter]);

  function handleMonthTabClick(nextMonth) {
    setDayFilter("");
    setMonthFilter(nextMonth);
  }

  function handleDayFilterChange(value) {
    setDayFilter(value);
    if (value) {
      const monthKey = value.slice(0, 7);
      setMonthFilter(monthKey);
    }
  }

  const branchOptions = useMemo(() => {
    const names = uniqueSorted(monthScopedAdvances.map((row) => row.branchName));
    const hasNoBranch = monthScopedAdvances.some((row) => !row.branchName);
    return hasNoBranch ? [...names, NO_BRANCH] : names;
  }, [monthScopedAdvances]);

  const departmentOptions = useMemo(() => {
    const scoped = branchFilter
      ? monthScopedAdvances.filter((row) => rowBranchKey(row) === branchFilter)
      : monthScopedAdvances;
    const names = uniqueSorted(scoped.map((row) => row.departmentName));
    const hasNoDepartment = scoped.some((row) => !row.departmentName);
    return hasNoDepartment ? [...names, NO_DEPARTMENT] : names;
  }, [monthScopedAdvances, branchFilter]);

  useEffect(() => {
    if (departmentFilter && !departmentOptions.includes(departmentFilter)) {
      setDepartmentFilter("");
    }
  }, [departmentFilter, departmentOptions]);

  useEffect(() => {
    if (branchFilter && !branchOptions.includes(branchFilter)) {
      setBranchFilter("");
    }
  }, [branchFilter, branchOptions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return monthScopedAdvances.filter((row) => {
      if (filter !== "all" && row.disbursementStatus !== filter) return false;
      if (branchFilter && rowBranchKey(row) !== branchFilter) return false;
      if (departmentFilter && rowDepartmentKey(row) !== departmentFilter) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        row.employeeName,
        row.employeeCode,
        row.departmentName,
        row.branchName,
        row.disbursementAssignee?.fullName,
        row.disbursementConfirmedBy?.fullName,
        row.reason,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [
    monthScopedAdvances,
    filter,
    search,
    branchFilter,
    departmentFilter,
  ]);

  const totalAmount = useMemo(
    () =>
      filtered.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [filtered],
  );

  function openEditModal(row) {
    const installments = Number(row.installments) || 1;
    const amount = Number(row.amount) || 0;
    setEditModal({
      open: true,
      row,
      amount: String(amount),
      installments: String(installments),
      installmentAmount:
        installments > 1 && amount > 0
          ? String(roundInstallment(amount, installments))
          : "",
      notes: "",
    });
    setEditError("");
  }

  function closeEditModal() {
    setEditModal({
      open: false,
      row: null,
      amount: "",
      installments: "",
      installmentAmount: "",
      notes: "",
    });
    setEditError("");
    setSaving(false);
  }

  function handleEditAmountChange(value) {
    setEditModal((prev) => {
      const count = Number(prev.installments) || 1;
      const next = { ...prev, amount: value };
      if (count > 1) {
        const total = Number(value);
        if (!Number.isNaN(total) && total > 0) {
          next.installmentAmount = String(roundInstallment(total, count));
        }
      }
      return next;
    });
  }

  function handleEditInstallmentsChange(value) {
    setEditModal((prev) => {
      const count = Number(value) || 1;
      const total = Number(prev.amount);
      const next = { ...prev, installments: value };
      if (count > 1 && !Number.isNaN(total) && total > 0) {
        next.installmentAmount = String(roundInstallment(total, count));
      }
      return next;
    });
  }

  function handleEditInstallmentAmountChange(value) {
    setEditModal((prev) => {
      const count = Number(prev.installments) || 1;
      const perInstallment = Number(value);
      const next = { ...prev, installmentAmount: value };
      if (
        count > 1 &&
        !Number.isNaN(perInstallment) &&
        perInstallment > 0
      ) {
        next.amount = String(roundInstallment(perInstallment * count, 1));
      }
      return next;
    });
  }

  async function handleSaveEdit() {
    const { row, amount, installments, installmentAmount, notes } = editModal;
    if (!row?.id) return;

    const parsedAmount = Number(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setEditError("أدخل مبلغاً صحيحاً أكبر من صفر");
      return;
    }

    const payload = {
      amount: parsedAmount,
      notes: notes.trim() || undefined,
    };

    if (Number(row.installments) > 1 || Number(installments) > 1) {
      const parsedInstallments = Number(installments);
      if (
        Number.isNaN(parsedInstallments) ||
        parsedInstallments < 2 ||
        parsedInstallments > 6
      ) {
        setEditError("عدد الأقساط للسلفة الاستثنائية يجب أن يكون بين 2 و 6");
        return;
      }
      payload.installments = parsedInstallments;

      const parsedInstallmentAmount = Number(installmentAmount);
      if (
        !Number.isNaN(parsedInstallmentAmount) &&
        parsedInstallmentAmount > 0
      ) {
        payload.installmentAmount = parsedInstallmentAmount;
      }
    }

    setSaving(true);
    setEditError("");
    try {
      await amendAdvance(row.id, payload);
      closeEditModal();
      await loadAdvances();
    } catch (err) {
      const message = err.response?.data?.message;
      setEditError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر تعديل السلفة",
      );
      setSaving(false);
    }
  }

  function canEditRow(row) {
    return canAmend && canAmendApprovedAdvance(row);
  }

  function canCancelRow(row) {
    return canCancelApprovedAdvance(row, user);
  }

  function canPostponeRow(row) {
    return canPostponeAdvanceDisbursement(row, user);
  }

  function canReturnRow(row) {
    return canRoute && row.disbursementStatus === "ASSIGNED";
  }

  function canReturnToHrRow(row) {
    return canReturnAdvanceToHr(row, user);
  }

  async function handleCancelRow(row) {
    if (!row?.id || rowBusyId) return;
    const ok = window.confirm(
      `إلغاء سلفة ${row.employeeName || "الموظف"} بمبلغ ${formatAmount(row.amount)}؟ لن يتم صرفها ويمكن للموظف تقديم طلب جديد.`,
    );
    if (!ok) return;

    setRowBusyId(row.id);
    try {
      await cancelApprovedAdvance(row.id);
      await loadAdvances();
    } catch (err) {
      const message = err.response?.data?.message;
      window.alert(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إلغاء السلفة",
      );
    } finally {
      setRowBusyId("");
    }
  }

  function openPostponeModal(row) {
    if (!row?.id || rowBusyId) return;
    setPostponeModal({
      open: true,
      row,
      date: addDaysToDateKey(localDateKey(), 1),
      reason: row.disbursementPostponeReason || "",
      error: "",
    });
  }

  function closePostponeModal() {
    if (rowBusyId) return;
    setPostponeModal({
      open: false,
      row: null,
      date: "",
      reason: "",
      error: "",
    });
  }

  async function handlePostponeSubmit() {
    const row = postponeModal.row;
    if (!row?.id || rowBusyId) return;

    const date = String(postponeModal.date || "").trim();
    const reason = String(postponeModal.reason || "").trim();
    const minDate = addDaysToDateKey(localDateKey(), 1);

    if (!date || date < minDate) {
      setPostponeModal((prev) => ({
        ...prev,
        error: "يجب اختيار يوم لاحق لتأجيل الصرف",
      }));
      return;
    }
    if (reason.length < 3) {
      setPostponeModal((prev) => ({
        ...prev,
        error: "سبب التأجيل إجباري",
      }));
      return;
    }

    setRowBusyId(row.id);
    setPostponeModal((prev) => ({ ...prev, error: "" }));
    try {
      await postponeAdvanceDisbursement(row.id, {
        postponedUntil: date,
        notes: reason,
      });
      setPostponeModal({
        open: false,
        row: null,
        date: "",
        reason: "",
        error: "",
      });
      await loadAdvances();
    } catch (err) {
      const message = err.response?.data?.message;
      setPostponeModal((prev) => ({
        ...prev,
        error: Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر تأجيل صرف السلفة",
      }));
    } finally {
      setRowBusyId("");
    }
  }

  async function handleReturnToHrRow(row) {
    if (!row?.id || rowBusyId) return;
    const ok = window.confirm(
      `إرجاع سلفة ${row.employeeName || "الموظف"} بمبلغ ${formatAmount(row.amount)} للموارد البشرية؟ لن تُصرف، ويمكن للموارد البشرية تعديلها أو إلغاؤها.`,
    );
    if (!ok) return;

    setRowBusyId(row.id);
    setRowBusyAction("return-hr");
    try {
      await returnAdvanceToHr(row.id);
      await loadAdvances();
    } catch (err) {
      const message = err.response?.data?.message;
      window.alert(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إرجاع السلفة للموارد البشرية",
      );
    } finally {
      setRowBusyId("");
      setRowBusyAction("");
    }
  }

  async function handleReturnRow(row) {
    if (!row?.id || rowBusyId) return;
    const ok = window.confirm(
      `إعادة توجيه سلفة ${row.employeeName || "الموظف"}؟ ستعود إلى قائمة توجيه الصرف.`,
    );
    if (!ok) return;

    setRowBusyId(row.id);
    setRowBusyAction("reroute");
    try {
      await returnAdvanceDisbursement(row.id);
      await loadAdvances();
    } catch (err) {
      const message = err.response?.data?.message;
      window.alert(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إعادة التوجيه",
      );
    } finally {
      setRowBusyId("");
      setRowBusyAction("");
    }
  }

  function handleExportExcel() {
    if (!filtered.length) return;

    const excelRows = filtered.map((row) => {
      const meta = statusMeta(row);
      const assignee = row.disbursementAssignee;
      const confirmedBy = row.disbursementConfirmedBy;

      return {
        الاسم: row.employeeName || "",
        الكود: row.employeeCode || "",
        القسم: row.departmentName || "",
        الفرع: row.branchName || "",
        الحالة: meta.label,
        المبلغ: Number(row.amount) || 0,
        النوع: advanceTypeLabel(row),
        الأقساط: Number(row.installments) || 1,
        "تاريخ الاعتماد": formatDate(row.approvedAt || row.submittedAt),
        "المكلَّف بالصرف": assignee?.fullName
          ? `${assignee.fullName}${
              assignee.jobTitle ? ` (${assignee.jobTitle})` : ""
            }`
          : "",
        "تاريخ التوجيه": formatDate(row.disbursementAssignedAt),
        "تاريخ الاستلام": formatDate(row.disbursementConfirmedAt),
        "أكّد الصرف": confirmedBy?.fullName
          ? `${confirmedBy.fullName}${
              confirmedBy.jobTitle ? ` (${confirmedBy.jobTitle})` : ""
            }`
          : "",
        السبب: row.reason || "",
      };
    });

    excelRows.push({
      الاسم: "الإجمالي",
      الكود: `${filtered.length} سلفة`,
      القسم: "",
      الفرع: "",
      الحالة: "",
      المبلغ: totalAmount,
      النوع: "",
      الأقساط: "",
      "تاريخ الاعتماد": "",
      "المكلَّف بالصرف": "",
      "تاريخ التوجيه": "",
      "تاريخ الاستلام": "",
      "أكّد الصرف": "",
      السبب: "",
    });

    const monthStamp = dayFilter
      ? dayFilter
      : monthFilter !== ALL_MONTHS
        ? monthFilter
        : new Date().toISOString().slice(0, 10);
    const sheetName = dayFilter
      ? `سجل صرف ${formatDayLabel(dayFilter)}`
      : monthFilter !== ALL_MONTHS
        ? `سجل صرف ${formatMonthLabel(monthFilter)}`
        : "سجل صرف السلف";
    exportExcel(excelRows, `سجل-صرف-السلف-${monthStamp}`, sheetName);
  }

  return (
    <div className="adv-disburse is-wide" dir="rtl">
      <div className="adv-disburse-header">
        <div>
          <h2 className="adv-disburse-title">سجل صرف السلف</h2>
          <p className="adv-disburse-subtitle">
            تفاصيل السلف المعتمدة وحالة التوجيه وتأكيد استلام المبلغ — مقسّمة
            حسب الشهر، مع فلتر يوم لاختيار تاريخ معيّن. التعديل والإلغاء متاحان
            للموارد البشرية قبل الصرف. المحاسب العام يمكنه تأجيل الصرف، أو
            إرجاع السلفة للموارد البشرية لتعديلها أو إلغائها، ولا يمكنه إلغاء
            السلفة بنفسه.
          </p>
        </div>
        <div className="adv-disburse-header-actions">
          <button
            type="button"
            className="adv-disburse-export"
            onClick={handleExportExcel}
            disabled={loading || filtered.length === 0}
          >
            تصدير Excel
          </button>
          <button
            type="button"
            className="adv-disburse-refresh"
            onClick={loadAdvances}
            disabled={loading}
          >
            تحديث
          </button>
        </div>
      </div>

      {monthTabs.length > 0 ? (
        <div className="adv-month-tabs" role="tablist" aria-label="أشهر السجل">
          <button
            type="button"
            role="tab"
            aria-selected={!dayFilter && monthFilter === ALL_MONTHS}
            className={`adv-month-tab${!dayFilter && monthFilter === ALL_MONTHS ? " is-active" : ""}`}
            onClick={() => handleMonthTabClick(ALL_MONTHS)}
            disabled={loading}
          >
            كل الأشهر
            <span className="adv-month-tab-count">{advances.length}</span>
          </button>
          {monthTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={!dayFilter && monthFilter === tab.id}
              className={`adv-month-tab${!dayFilter && monthFilter === tab.id ? " is-active" : ""}`}
              onClick={() => handleMonthTabClick(tab.id)}
              disabled={loading}
            >
              {tab.label}
              <span className="adv-month-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="adv-disburse-toolbar">
        <label className="adv-disburse-day-filter">
          <span className="adv-disburse-day-filter-label">يوم الصرف</span>
          <input
            type="date"
            className="adv-disburse-filter-select"
            value={dayFilter}
            onChange={(e) => handleDayFilterChange(e.target.value)}
            disabled={loading || advances.length === 0}
            aria-label="فلتر يوم الصرف"
          />
          {dayFilter ? (
            <button
              type="button"
              className="adv-disburse-day-clear"
              onClick={() => setDayFilter("")}
              disabled={loading}
            >
              مسح اليوم
            </button>
          ) : null}
        </label>
        <input
          type="search"
          className="adv-disburse-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالاسم أو الكود أو المكلَّف..."
          disabled={loading}
        />
        <select
          className="adv-disburse-filter-select"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          disabled={loading || monthScopedAdvances.length === 0}
          aria-label="فلتر الفرع"
        >
          <option value="">كل الفروع</option>
          {branchOptions.map((name) => (
            <option key={name} value={name}>
              {name === NO_BRANCH ? "بدون فرع (إدارة)" : name}
            </option>
          ))}
        </select>
        <select
          className="adv-disburse-filter-select"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          disabled={loading || monthScopedAdvances.length === 0}
          aria-label="فلتر القسم"
        >
          <option value="">كل الأقسام</option>
          {departmentOptions.map((name) => (
            <option key={name} value={name}>
              {name === NO_DEPARTMENT ? "بدون قسم" : name}
            </option>
          ))}
        </select>
        <div className="adv-ledger-filters">
          {[
            { id: "all", label: "الكل" },
            { id: "PENDING_ASSIGNMENT", label: "بانتظار التوجيه" },
            { id: "ASSIGNED", label: "بانتظار التأكيد" },
            { id: "RETURNED_TO_HR", label: "مُعادة للـ HR" },
            { id: "DISBURSED", label: "تم الصرف" },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              className={`adv-ledger-filter${filter === item.id ? " is-active" : ""}`}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="adv-disburse-count">
          {filtered.length} من {monthScopedAdvances.length}
        </span>
      </div>

      {loading ? (
        <div className="adv-disburse-empty">جاري التحميل...</div>
      ) : loadError ? (
        <div className="adv-disburse-error-banner">{loadError}</div>
      ) : filtered.length === 0 ? (
        <div className="adv-disburse-empty">
          {advances.length === 0
            ? "لا توجد سلف في هذا السجل"
            : monthScopedAdvances.length === 0
              ? dayFilter
                ? `لا توجد سلف في يوم ${formatDayLabel(dayFilter)}`
                : "لا توجد سلف في هذا الشهر"
              : "لا توجد نتائج مطابقة للبحث أو الفلاتر"}
        </div>
      ) : (
        <div className="adv-disburse-table-wrap">
          <table className="adv-disburse-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>الكود</th>
                <th>الفرع</th>
                <th>الحالة</th>
                <th>المبلغ</th>
                <th>النوع</th>
                <th>تاريخ الاعتماد</th>
                <th>المكلَّف بالصرف</th>
                <th>تاريخ التوجيه</th>
                <th>تاريخ الاستلام</th>
                <th>أكّد الصرف</th>
                {showActions ? <th>إجراءات</th> : null}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const meta = statusMeta(row);
                return (
                  <tr key={row.id}>
                    <td className="is-name">
                      <div className="adv-disburse-name-cell">
                        <strong>{row.employeeName || "—"}</strong>
                        {row.departmentName ? (
                          <span className="adv-disburse-reason-hint">
                            {row.departmentName}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>{row.employeeCode || "—"}</td>
                    <td>{row.branchName || "—"}</td>
                    <td>
                      <span className={`adv-disburse-badge ${meta.className}`}>
                        {meta.label}
                      </span>
                      {isAdvanceDisbursementPostponed(row) ? (
                        <span className="adv-postpone-hint">
                          حتى {formatDate(row.disbursementPostponedUntil)}
                          {row.disbursementPostponeReason
                            ? ` — ${row.disbursementPostponeReason}`
                            : ""}
                        </span>
                      ) : null}
                    </td>
                    <td>{formatAmount(row.amount)}</td>
                    <td>{advanceTypeLabel(row)}</td>
                    <td>{formatDate(row.approvedAt || row.submittedAt)}</td>
                    <td>{assigneeLabel(row)}</td>
                    <td>{formatDate(row.disbursementAssignedAt)}</td>
                    <td>{formatDate(row.disbursementConfirmedAt)}</td>
                    <td>{personLabel(row.disbursementConfirmedBy)}</td>
                    {showActions ? (
                      <td>
                        <div className="adv-row-actions">
                          {canEditRow(row) ? (
                            <button
                              type="button"
                              className="adv-ledger-edit-btn"
                              onClick={() => openEditModal(row)}
                              disabled={Boolean(rowBusyId)}
                            >
                              تعديل
                            </button>
                          ) : null}
                          {canReturnRow(row) ? (
                            <button
                              type="button"
                              className="adv-ledger-edit-btn"
                              onClick={() => handleReturnRow(row)}
                              disabled={Boolean(rowBusyId)}
                            >
                              {rowBusyId === row.id &&
                              rowBusyAction === "reroute"
                                ? "جاري..."
                                : "إعادة توجيه"}
                            </button>
                          ) : null}
                          {canReturnToHrRow(row) ? (
                            <button
                              type="button"
                              className="adv-ledger-edit-btn is-danger"
                              onClick={() => handleReturnToHrRow(row)}
                              disabled={Boolean(rowBusyId)}
                            >
                              {rowBusyId === row.id &&
                              rowBusyAction === "return-hr"
                                ? "جاري..."
                                : "إرجاع للموارد البشرية"}
                            </button>
                          ) : null}
                          {canPostponeRow(row) ? (
                            <button
                              type="button"
                              className="adv-ledger-edit-btn"
                              onClick={() => openPostponeModal(row)}
                              disabled={Boolean(rowBusyId)}
                            >
                              تأجيل
                            </button>
                          ) : null}
                          {canCancelRow(row) ? (
                            <button
                              type="button"
                              className="adv-ledger-edit-btn is-danger"
                              onClick={() => handleCancelRow(row)}
                              disabled={Boolean(rowBusyId)}
                            >
                              {rowBusyId === row.id ? "جاري..." : "إلغاء"}
                            </button>
                          ) : null}
                          {!canEditRow(row) &&
                          !canReturnRow(row) &&
                          !canReturnToHrRow(row) &&
                          !canPostponeRow(row) &&
                          !canCancelRow(row) ? (
                            <span className="adv-disburse-reason-hint">
                              {row.disbursementStatus === "DISBURSED"
                                ? "تم الصرف"
                                : Number(row.installmentsPaid ?? 0) > 0
                                  ? "بدأ السداد"
                                  : "—"}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="is-total-label">
                  الإجمالي ({filtered.length} سلفة)
                </td>
                <td className="is-total-amount">{formatAmount(totalAmount)}</td>
                <td colSpan={showActions ? 7 : 6} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {editModal.open ? (
        <div className="adv-edit-modal-overlay" onClick={closeEditModal}>
          <div
            className="adv-edit-modal"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <h3 className="adv-edit-modal-title">تعديل السلفة المعتمدة</h3>
            <p className="adv-edit-modal-subtitle">
              {editModal.row?.employeeName || "موظف"} —{" "}
              {advanceTypeLabel(editModal.row || {})}
              {editModal.row?.disbursementStatus === "RETURNED_TO_HR"
                ? " — بعد الحفظ تعود السلفة لقائمة توجيه المحاسب العام"
                : editModal.row?.disbursementStatus === "ASSIGNED"
                  ? " — لم تُصرف بعد"
                  : ""}
            </p>

            <div className="adv-edit-modal-field">
              <label className="adv-edit-modal-label">المبلغ الإجمالي</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="adv-edit-modal-input"
                value={editModal.amount}
                onChange={(e) => handleEditAmountChange(e.target.value)}
                disabled={saving}
              />
            </div>

            {editModal.row?.isExceptional || Number(editModal.installments) > 1 ? (
              <>
                <div className="adv-edit-modal-field">
                  <label className="adv-edit-modal-label">عدد الأقساط</label>
                  <input
                    type="number"
                    min="2"
                    max="6"
                    step="1"
                    className="adv-edit-modal-input"
                    value={editModal.installments}
                    onChange={(e) =>
                      handleEditInstallmentsChange(e.target.value)
                    }
                    disabled={saving}
                  />
                </div>
                <div className="adv-edit-modal-field">
                  <label className="adv-edit-modal-label">مبلغ القسط</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="adv-edit-modal-input"
                    value={editModal.installmentAmount}
                    onChange={(e) =>
                      handleEditInstallmentAmountChange(e.target.value)
                    }
                    disabled={saving}
                  />
                </div>
              </>
            ) : null}

            <div className="adv-edit-modal-field">
              <label className="adv-edit-modal-label">ملاحظات (اختياري)</label>
              <textarea
                className="adv-edit-modal-textarea"
                rows={3}
                value={editModal.notes}
                onChange={(e) =>
                  setEditModal((prev) => ({ ...prev, notes: e.target.value }))
                }
                disabled={saving}
              />
            </div>

            {editError ? (
              <p className="adv-edit-modal-error">{editError}</p>
            ) : null}

            <div className="adv-edit-modal-buttons">
              <button
                type="button"
                className="adv-edit-modal-btn secondary"
                onClick={closeEditModal}
                disabled={saving}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="adv-edit-modal-btn primary"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? "جاري الحفظ..." : "حفظ التعديل"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdvancePostponeModal
        open={postponeModal.open}
        row={postponeModal.row}
        date={postponeModal.date}
        reason={postponeModal.reason}
        error={postponeModal.error}
        submitting={Boolean(rowBusyId) && postponeModal.open}
        minDate={addDaysToDateKey(localDateKey(), 1)}
        amountLabel={
          postponeModal.row ? formatAmount(postponeModal.row.amount) : ""
        }
        onDateChange={(value) =>
          setPostponeModal((prev) => ({ ...prev, date: value, error: "" }))
        }
        onReasonChange={(value) =>
          setPostponeModal((prev) => ({ ...prev, reason: value, error: "" }))
        }
        onClose={closePostponeModal}
        onSubmit={handlePostponeSubmit}
      />
    </div>
  );
}
